import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import { Contact } from '../types';

interface ContactsPanelProps {
  contacts: Contact[];
  onStartConversation: (contactId: string) => void;
  onClose: () => void;
}

const ContactsPanel = ({ contacts, onStartConversation, onClose }: ContactsPanelProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const roleOptions = [
    { value: 'all', label: 'Toate rolurile' },
    { value: 'admin', label: 'Administratori' },
    { value: 'secretariat', label: 'Secretariat' },
    { value: 'teacher', label: 'Profesori' },
    { value: 'student', label: 'Elevi' },
    { value: 'parent', label: 'Părinți' },
  ];

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch = contact.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || contact.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    const key = contact.role;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(contact);
    return acc;
  }, {} as Record<string, Contact[]>);

  const getClassLabel = (value?: string) => value?.trim() || 'Fără clasă';

  const parentClassGroup = (contact: Contact) => {
    const classes = (contact.children ?? [])
      .map((c) => c.classroomName?.trim())
      .filter(Boolean) as string[];
    const unique = Array.from(new Set(classes));
    if (unique.length === 0) return 'Fără clasă';
    if (unique.length === 1) return unique[0];
    return 'Mai multe clase';
  };

  const groupByClass = (items: Contact[], keyFn: (c: Contact) => string) => {
    const result = new Map<string, Contact[]>();
    for (const c of items) {
      const key = keyFn(c);
      result.set(key, [...(result.get(key) ?? []), c]);
    }
    return Array.from(result.entries()).sort(([a], [b]) => a.localeCompare(b, 'ro-RO'));
  };

  const sections = useMemo(() => {
    const admins = (groupedContacts.admin ?? []).slice();
    const secretariat = (groupedContacts.secretariat ?? []).slice();
    const teachers = (groupedContacts.teacher ?? []).slice();
    const students = (groupedContacts.student ?? []).slice();
    const parents = (groupedContacts.parent ?? []).slice();

    const sortContacts = (a: Contact, b: Contact) =>
      Number(b.isOnline) - Number(a.isOnline) || a.name.localeCompare(b.name, 'ro-RO');

    admins.sort(sortContacts);
    secretariat.sort(sortContacts);
    teachers.sort(sortContacts);
    students.sort(sortContacts);
    parents.sort(sortContacts);

    const studentGroups = groupByClass(students, (c) => getClassLabel(c.grade));
    const parentGroups = groupByClass(parents, (c) => parentClassGroup(c));

    return [
      { title: 'Administratori', items: admins },
      { title: 'Secretariat', items: secretariat },
      { title: 'Profesori', items: teachers },
      ...studentGroups.map(([cls, items]) => ({ title: `Elevi • ${cls}`, items })),
      ...parentGroups.map(([cls, items]) => ({ title: `Părinți • ${cls}`, items })),
    ].filter((s) => s.items.length > 0);
  }, [groupedContacts]);

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administratori';
      case 'secretariat':
        return 'Secretariat';
      case 'teacher':
        return 'Profesori';
      case 'student':
        return 'Elevi';
      case 'parent':
        return 'Părinți';
      default:
        return role;
    }
  };

  const getRoleLabelSingle = (role: string) => {
    switch (role) {
      case 'admin':
        return 'administrator';
      case 'secretariat':
        return 'secretariat';
      case 'teacher':
        return 'profesor';
      case 'student':
        return 'elev';
      case 'parent':
        return 'părinte';
      default:
        return role;
    }
  };

  const renderParentChildren = (contact: Contact) => {
    const children = contact.children ?? [];
    if (children.length === 0) return null;
    return (
      <div className="mt-0.5 text-xs text-muted-foreground leading-4">
        <div className="font-medium text-muted-foreground/90">Părinte:</div>
        <div className="mt-0.5 space-y-0.5">
          {children.map((c) => (
            <div key={c.id} className="whitespace-normal break-words">
              {c.name}
              {c.classroomName ? <span className="opacity-80"> ({c.classroomName})</span> : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-card border-l border-border">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Contacte</h2>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Închide panoul de contacte"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-3">
          <Input
            type="search"
            placeholder="Caută contacte..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <Select
            options={roleOptions}
            value={roleFilter}
            onChange={(value) => setRoleFilter(value as string)}
            placeholder="Filtrează după rol"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6">
            <Icon name="Users" size={48} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground text-center">
              {searchQuery || roleFilter !== 'all'
                ? 'Nu s-au găsit contacte'
                : 'Nu există contacte disponibile'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {sections.map((section) => (
              <div key={section.title}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {section.title} ({section.items.length})
                </h3>
                <div className="space-y-2">
                  {section.items.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => onStartConversation(contact.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-muted transition-colors duration-150"
                    >
                      <div className="relative flex-shrink-0">
                        {contact.avatar ? (
                          <Image
                            src={contact.avatar}
                            alt={`${contact.name} avatar`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                            {contact.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        {contact.isOnline && (
                          <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-foreground truncate">
                          {contact.name}
                        </p>
                        <div className="text-xs text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <span className="capitalize">{getRoleLabelSingle(contact.role)}</span>
                            {contact.role !== 'parent' && contact.department && (
                              <>
                                <span>•</span>
                                <span>{contact.department}</span>
                              </>
                            )}
                            {contact.role !== 'parent' && contact.grade && (
                              <>
                                <span>•</span>
                                <span>{contact.grade}</span>
                              </>
                            )}
                          </div>
                          {contact.role === 'parent' && renderParentChildren(contact)}
                        </div>
                      </div>

                      <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border">
        <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Sunt vizibile doar contactele la care ai acces. Pornește o conversație pentru a începe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactsPanel;
